import React from "react";
// Cache buster
import styled, { useTheme } from "styled-components";
import { motion } from "framer-motion";

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.bg};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const LoaderWrapper = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const LoaderOne = () => {
  const theme = useTheme();

  return (
    <Container>
      <LoaderWrapper>
        {/* Outer Ring - Dashed */}
        <motion.div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            border: `2px dashed ${theme.text_secondary}`,
            opacity: 0.2,
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Middle Ring - Primary Color Arc */}
        <motion.div
          style={{
            position: "absolute",
            width: "70%",
            height: "70%",
            borderRadius: "50%",
            borderTop: `4px solid ${theme.primary}`,
            borderRight: `4px solid transparent`,
            borderBottom: `4px solid transparent`,
            borderLeft: `4px solid transparent`,
            boxShadow: `0 0 20px ${theme.primary}40`, // Soft glow
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Inner Ring - Counter Rotating */}
        <motion.div
          style={{
            position: "absolute",
            width: "40%",
            height: "40%",
            borderRadius: "50%",
            borderBottom: `4px solid ${theme.primary}`,
            borderTop: `4px solid transparent`,
            borderRight: `4px solid transparent`,
            borderLeft: `4px solid transparent`,
            opacity: 0.8,
          }}
          animate={{ rotate: -360 }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Center Dot - Pulsing */}
        <motion.div
          style={{
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            backgroundColor: theme.primary,
            boxShadow: `0 0 15px ${theme.primary}`,
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </LoaderWrapper>
    </Container>
  );
};

export const LoaderThree = () => {
  const theme = useTheme();

  return (
    <Container>
      <LoaderWrapper>
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            style={{
              width: "16px",
              height: "16px",
              margin: "0 6px",
              borderRadius: "50%",
              backgroundColor: theme.primary, // Using theme primary color
              opacity: 1,
            }}
            animate={{
              y: ["0%", "-100%", "0%"],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.2,
            }}
          />
        ))}
      </LoaderWrapper>
    </Container>
  );
};

export const LoaderTwo = () => {
  const theme = useTheme();

  return (
    <Container>
      <LoaderWrapper>
        {/* Central Nucleus */}
        <motion.div
          style={{
            position: "absolute",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: theme.primary,
            boxShadow: `0 0 20px ${theme.primary}`,
            zIndex: 10,
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.8, 1, 0.8],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Electron 1 */}
        <motion.div
          style={{
            position: "absolute",
            width: "100%",
            height: "30%",
            borderRadius: "50%",
            border: `1px solid ${theme.primary}60`,
            boxShadow: `0 0 10px ${theme.primary}20`,
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear",
          }}
        >
           <motion.div
            style={{
              position: "absolute",
              top: "50%",
              left: "100%",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: theme.white,
              boxShadow: `0 0 10px ${theme.white}`,
              transform: "translate(-50%, -50%)",
            }}
          />
        </motion.div>

        {/* Electron 2 */}
        <motion.div
          style={{
            position: "absolute",
            width: "100%",
            height: "30%",
            borderRadius: "50%",
            border: `1px solid ${theme.primary}60`,
            boxShadow: `0 0 10px ${theme.primary}20`,
          }}
          initial={{ rotate: 60 }}
          animate={{ rotate: 420 }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear",
            delay: 0.5,
          }}
        >
          <motion.div
            style={{
              position: "absolute",
              top: "50%",
              left: "0%", // Opposite side
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: theme.white,
              boxShadow: `0 0 10px ${theme.white}`,
              transform: "translate(-50%, -50%)",
            }}
          />
        </motion.div>

        {/* Electron 3 */}
        <motion.div
          style={{
            position: "absolute",
            width: "100%",
            height: "30%",
            borderRadius: "50%",
            border: `1px solid ${theme.primary}60`,
            boxShadow: `0 0 10px ${theme.primary}20`,
          }}
          initial={{ rotate: 120 }}
          animate={{ rotate: 480 }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear",
            delay: 1,
          }}
        >
           <motion.div
            style={{
              position: "absolute",
              top: "50%",
              left: "100%",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: theme.white,
              boxShadow: `0 0 10px ${theme.white}`,
              transform: "translate(-50%, -50%)",
            }}
          />
        </motion.div>
      </LoaderWrapper>
    </Container>
  );
};
